import React, { useState, useMemo, useEffect } from 'react';
import { useFieldArray } from 'react-hook-form';
import axios from 'axios';
import { API_BASE_URL } from '../infra/apiConfig';

// Lista de meses para o dropdown e para a ordenação
const mesesDoAno = [
  { valor: 1, nome: 'Janeiro' }, { valor: 2, nome: 'Fevereiro' },
  { valor: 3, nome: 'Março' }, { valor: 4, nome: 'Abril' },
  { valor: 5, nome: 'Maio' }, { valor: 6, nome: 'Junho' },
  { valor: 7, nome: 'Julho' }, { valor: 8, nome: 'Agosto' },
  { valor: 9, nome: 'Setembro' }, { valor: 10, nome: 'Outubro' },
  { valor: 11, nome: 'Novembro' }, { valor: 12, nome: 'Dezembro' },
];

// ✅ COMPONENTE ATUALIZADO: inicia sempre fechado
const EditableTextarea = ({ name, label, description, register, error, value, onSave, ...rest }) => {
  const [isEditing, setIsEditing] = useState(false); // sempre fechado inicialmente

  const handleSave = () => {
    if (onSave) {
      onSave(name);
    }
    setIsEditing(false);
  };

  return (
    <div className="w-full">
      <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {description && (
        <p className="text-sm text-gray-500 mb-2">{description}</p>
      )}

      {isEditing ? (
        // MODO DE EDIÇÃO
        <div>
          <textarea
            id={name}
            rows="4"
            {...register(name)}
            className="w-full p-2 bg-white border border-gray-300 rounded-md text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            {...rest}
          ></textarea>
          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="px-3 py-1 text-sm text-gray-700 mr-2"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-4 py-1 bg-green-600 text-white text-sm font-semibold rounded-md hover:bg-green-700"
            >
              Salvar
            </button>
          </div>
        </div>
      ) : (
        // MODO DE VISUALIZAÇÃO
        <div className="group relative">
          <div className="w-full p-2 bg-gray-50 border border-gray-200 rounded-md min-h-[105px] text-gray-800 whitespace-pre-wrap">
            {value || <span className="text-gray-400">Nenhum conteúdo. Clique em editar para adicionar.</span>}
          </div>
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="absolute top-2 right-2 px-3 py-1 bg-blue-600 text-white text-sm font-semibold rounded-md hover:bg-blue-700 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            Editar
          </button>
        </div>
      )}

      {error && <span className="text-red-600 text-sm mt-1">{error.message}</span>}
    </div>
  );
};

// Componente Input
const Input = ({ name, label, register, required, error, ...rest }) => (
  <div className="w-full">
    <label htmlFor={name} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input
      id={name}
      {...register(name, { required })}
      className="w-full p-2 bg-white border border-gray-300 rounded-md text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      {...rest}
    />
    {error && <span className="text-red-600 text-sm mt-1">{error.message}</span>}
  </div>
);

const Step2_DetalhesProjeto = ({ register, errors, control, watch, getValues, reset }) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "etapas",
    rules: { maxLength: { value: 5, message: "Você pode adicionar no máximo 5 meses." } }
  });

  useEffect(() => {
    const fetchProjeto = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API_BASE_URL}/meu-projeto`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data) {
          console.log('Projeto existente:', res.data);
          reset(prev => ({
            ...prev,
            nomeIniciativa: res.data.nome_iniciativa,
            areaTematica: res.data.area_tematica,
            problemaObjetivo: res.data.problema_objetivo,
            publicoBeneficiario: res.data.publico_beneficiario,
            caraterInovacao: res.data.carater_inovacao,
            impactosPrevistos: res.data.impactos_previstos,
            potencialExecucao: res.data.potencial_execucao,
            contribuicaoParceria: res.data.contribuicao_parceria,
            etapas: res.data.etapas || []
          }));
        }
      } catch (err) {
        console.error('Erro ao buscar projeto:', err);
      }
    };

    fetchProjeto();
  }, [reset]);

  const etapasValues = watch("etapas") || [];

  const handleSaveField = async (fieldName) => {
    const fieldValue = getValues(fieldName);
    console.log(`Salvando campo "${fieldName}" com valor:`, fieldValue);
    await new Promise(resolve => setTimeout(resolve, 500));
    console.log(`Campo "${fieldName}" salvo com sucesso!`);
  };

  const sortedFields = useMemo(() => {
    const combined = fields.map((field, index) => ({
      ...field,
      ...etapasValues[index]
    }));
    return combined.sort((a, b) => a.mes - b.mes);
  }, [fields, etapasValues]);

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-gray-800">2. Detalhes do Protótipo do Projeto</h2>

      <Input
        name="nomeIniciativa"
        label="Nome da Iniciativa/Projeto"
        register={register}
        required="O nome da iniciativa é obrigatório."
        error={errors.nomeIniciativa}
      />

      <div>
        <h3 className="block text-sm font-medium text-gray-700 mb-2">Área Temática (selecione apenas uma)</h3>
        <div className="space-y-2">
          {['devWeb', 'redesTI', 'comunicacaoMidia'].map(value => (
            <label key={value} className="flex items-center text-gray-600">
              <input
                type="radio"
                value={value}
                {...register("areaTematica", { required: "Selecione uma área." })}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
              />
              <span className="ml-2">
                {value === 'devWeb'
                  ? 'Programação e Desenvolvimento Web'
                  : value === 'redesTI'
                    ? 'Redes e Tecnologia da Informação'
                    : 'Comunicação e Mídias Digitais'}
              </span>
            </label>
          ))}
        </div>

        {errors.areaTematica && <span className="text-red-600 text-sm mt-1">{errors.areaTematica.message}</span>}
      </div>

      {/* Textareas (agora iniciam sempre fechados) */}
      <EditableTextarea
        name="problemaObjetivo"
        label="a) Apresente o objetivo central do protótipo de projeto"
        description="Descrição clara do problema concreto que o projeto busca solucionar em áreas como cultura, educação, saúde, agricultura, cidadania, esporte e lazer no território de Lagoa de Itaenga."
        register={register}
        required
        error={errors.problemaObjetivo}
        value={watch('problemaObjetivo')}
        onSave={handleSaveField}
      />
      <EditableTextarea
        name="publicoBeneficiario"
        label="b) Apresentar o público que será beneficiado como o protótipo de projeto"
        description="Apresentar o público que será beneficiado como o protótipo de projeto."
        register={register}
        required
        error={errors.publicoBeneficiario}
        value={watch('publicoBeneficiario')}
        onSave={handleSaveField}
      />
      <EditableTextarea
        name="caraterInovacao"
        label="c) Apresente o caráter inovador do protótipo de projeto para o território de Lagoa de Itaenga."
        description="Explicação de por que a solução é inovadora para o contexto de Lagoa de Itaenga."
        register={register}
        required
        error={errors.caraterInovacao}
        value={watch('caraterInovacao')}
        onSave={handleSaveField}
      />
      <EditableTextarea
        name="impactosPrevistos"
        label="d) Impactos Previstos na Sociedade"
        description="Apresente a relevância e impacto (Viabilidade) do projeto no desenvolvimento para o território de Lagoa de Itaenga."
        register={register}
        required
        error={errors.impactosPrevistos}
        value={watch('impactosPrevistos')}
        onSave={handleSaveField}
      />
      <EditableTextarea
        name="potencialExecucao"
        label="e) Proposição da solução para um problema concreto"
        description="Vivenciado por uma comunidade ou grupo social (ligadas a cultura, educação, saúde, agricultura, cidadania, esporte e lazer ou áreas afins), no território de Lagoa de Itaenga."
        register={register}
        required
        error={errors.potencialExecucao}
        value={watch('potencialExecucao')}
        onSave={handleSaveField}
      />
      <EditableTextarea
        name="contribuicaoParceria"
        label="f) Contribuição e Parceria"
        description="Indicar possível contribuição e parceria para o desenvolvimento econômico e social do projeto."
        register={register}
        required
        error={errors.contribuicaoParceria}
        value={watch('contribuicaoParceria')}
        onSave={handleSaveField}
      />

      {/* ✅ Etapas de desenvolvimento com layout corrigido */}
      <div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">Etapas de Desenvolvimento e Implantação</h3>

        {sortedFields.map((item) => {
          const originalIndex = fields.findIndex(field => field.id === item.id);
          return (
            <div key={item.id} className="p-4 border rounded-lg my-4 bg-gray-50 space-y-4">
              <div className="flex justify-between items-start">
                <h4 className="text-sm font-medium text-gray-700">Etapa {originalIndex + 1}</h4>
                <button
                  type="button"
                  onClick={() => remove(originalIndex)}
                  className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700"
                >
                  Remover
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-1">
                  <label className="block text-sm font-medium text-gray-700">Mês</label>
                  <select {...register(`etapas.${originalIndex}.mes`, { required: "Selecione um mês." })} className="mt-1 w-full p-2 border border-gray-300 rounded-md">
                    <option value="">Selecione...</option>
                    {mesesDoAno.map(mes => (<option key={mes.valor} value={mes.valor}>{mes.nome}</option>))}
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Ação Planejada</label>
                  <textarea
                    {...register(`etapas.${originalIndex}.acao`, { required: "Descreva a ação." })}
                    rows="2"
                    className="mt-1 w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
            </div>
          );
        })}

        <button
          type="button"
          onClick={() => append({ mes: '', acao: '' })}
          disabled={fields.length >= 5}
          className="mt-2 px-4 py-2 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700 disabled:bg-gray-400"
        >
          Adicionar Mês
        </button>

        {errors.etapas && (
          <p className="mt-2 text-sm text-red-600">
            {errors.etapas.root?.message || errors.etapas.message}
          </p>
        )}
      </div>
    </div>
  );
};

export default Step2_DetalhesProjeto;
